import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Envelope, MapPin, Globe, Clock, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const contactInfo = [
  { icon: <Phone size={20} weight="duotone" />, label: "Phone", value: "+27 74 836 0898", href: "tel:+27748360898" },
  { icon: <Phone size={20} weight="duotone" />, label: "Fax / Alt", value: "+27 86 628 9612", href: "tel:+27866289612" },
  { icon: <Envelope size={20} weight="duotone" />, label: "Email", value: "info@kznsc.co.za", href: "mailto:info@kznsc.co.za" },
  { icon: <Globe size={20} weight="duotone" />, label: "Website", value: "www.kznsc.co.za", href: "https://www.kznsc.co.za" },
  { icon: <MapPin size={20} weight="duotone" />, label: "Address", value: "Unit 14 Halstead Park, 15 Halstead Road, Mkodeni, Pietermaritzburg, 3201", href: null },
  { icon: <Clock size={20} weight="duotone" />, label: "Hours", value: "Monday - Friday, 08:00 - 17:00", href: null },
];

export default function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, formData);
      setStatus("success");
      setFormData({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative py-24 lg:py-32 bg-[#0B2D2D] overflow-hidden noise-overlay" data-testid="contact-hero">
        <div className="absolute inset-0 pattern-grid opacity-20" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#A7D129] mb-4">Get In Touch</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading font-black text-5xl md:text-7xl tracking-tighter text-white leading-[0.9] mb-6">Contact Us</motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-[#E0F5F2]/70 max-w-2xl leading-relaxed">
              Ready to start your next infrastructure project? Get in touch with our team for quotes, product information, or technical support.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="py-20 lg:py-28 bg-white" data-testid="contact-form-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Form */}
            <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="bg-white border border-[#0F4C5C]/20 p-8 lg:p-10">
                <h2 className="font-heading font-bold text-2xl text-[#0B2D2D] mb-2">Send Us A Message</h2>
                <p className="text-sm text-[#0B2D2D]/50 mb-8">Fill out the form below and we'll get back to you promptly.</p>

                {status === "success" ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <CheckCircle size={48} weight="duotone" className="text-[#A7D129] mx-auto mb-4" />
                    <h3 className="font-heading font-bold text-xl text-[#0B2D2D] mb-2">Message Sent Successfully</h3>
                    <p className="text-sm text-[#0B2D2D]/60">Thank you for reaching out. Our team will respond within 24 hours.</p>
                    <button onClick={() => setStatus("idle")} className="mt-6 text-[#00A091] font-bold text-sm hover:text-[#A7D129] transition-colors" data-testid="send-another-btn">
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0B2D2D]/60 font-bold mb-2 block">Full Name *</label>
                        <Input name="name" value={formData.name} onChange={handleChange} required placeholder="Your full name" className="rounded-none border-[#0F4C5C]/30 focus-visible:ring-[#00A091] bg-[#E0F5F2]/30" data-testid="input-name" />
                      </div>
                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0B2D2D]/60 font-bold mb-2 block">Email Address *</label>
                        <Input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com" className="rounded-none border-[#0F4C5C]/30 focus-visible:ring-[#00A091] bg-[#E0F5F2]/30" data-testid="input-email" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0B2D2D]/60 font-bold mb-2 block">Phone</label>
                        <Input name="phone" value={formData.phone} onChange={handleChange} placeholder="+27 ..." className="rounded-none border-[#0F4C5C]/30 focus-visible:ring-[#00A091] bg-[#E0F5F2]/30" data-testid="input-phone" />
                      </div>
                      <div>
                        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0B2D2D]/60 font-bold mb-2 block">Company</label>
                        <Input name="company" value={formData.company} onChange={handleChange} placeholder="Company name" className="rounded-none border-[#0F4C5C]/30 focus-visible:ring-[#00A091] bg-[#E0F5F2]/30" data-testid="input-company" />
                      </div>
                    </div>
                    <div>
                      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0B2D2D]/60 font-bold mb-2 block">Subject *</label>
                      <Input name="subject" value={formData.subject} onChange={handleChange} required placeholder="Quote request, product inquiry, etc." className="rounded-none border-[#0F4C5C]/30 focus-visible:ring-[#00A091] bg-[#E0F5F2]/30" data-testid="input-subject" />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0B2D2D]/60 font-bold mb-2 block">Message *</label>
                      <Textarea name="message" value={formData.message} onChange={handleChange} required placeholder="Tell us about your project or inquiry..." rows={5} className="rounded-none border-[#0F4C5C]/30 focus-visible:ring-[#00A091] bg-[#E0F5F2]/30" data-testid="input-message" />
                    </div>
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#A7D129] text-[#0B2D2D] font-bold uppercase tracking-widest text-xs px-10 py-4 hover:bg-[#00A091] hover:text-white transition-colors disabled:opacity-50"
                      data-testid="submit-contact-btn"
                    >
                      {status === "loading" ? "Sending..." : (<><PaperPlaneTilt size={16} weight="bold" /> Send Message</>)}
                    </button>
                    {status === "error" && (
                      <p className="text-sm text-red-600 mt-2" data-testid="contact-error">Something went wrong. Please try again or contact us directly.</p>
                    )}
                  </form>
                )}
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div className="lg:col-span-5" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <div className="bg-[#0B2D2D] p-8 lg:p-10 h-full">
                <h3 className="font-heading font-bold text-xl text-white mb-6">Contact Information</h3>
                <div className="space-y-5">
                  {contactInfo.map((info, i) => (
                    <div key={i} className="flex items-start gap-4" data-testid={`contact-info-${i}`}>
                      <div className="text-[#A7D129] mt-0.5 flex-shrink-0">{info.icon}</div>
                      <div>
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#4EC9BE] mb-0.5">{info.label}</p>
                        {info.href ? (
                          <a href={info.href} target={info.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-sm text-[#E0F5F2] hover:text-[#A7D129] transition-colors">
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-sm text-[#E0F5F2]/80">{info.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <Separator className="bg-white/10 my-8" />

                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A7D129] font-bold mb-3">Company Registration</p>
                  <div className="space-y-2 text-sm text-[#E0F5F2]/60">
                    <p>Reg: 2011/076665</p>
                    <p>VAT: 4940263272</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* GOOGLE MAPS */}
      <section className="py-0" data-testid="contact-map">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00A091] mb-3">Find Us</p>
            <h2 className="font-heading font-bold text-2xl md:text-4xl tracking-tight text-[#0B2D2D] mb-6">Our Location</h2>
            <div className="border border-[#0F4C5C]/20 overflow-hidden">
              <iframe
                title="KZNSC Location - Halstead Park, Pietermaritzburg"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3463.5!2d30.3895!3d-29.5995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1ef6a563b8c7e8c7%3A0x8f0e9b0e2e0e0e0e!2sHalstead+Rd%2C+Mkondeni%2C+Pietermaritzburg%2C+3201!5e0!3m2!1sen!2sza!4v1700000000000!5m2!1sen!2sza"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
                data-testid="google-map-iframe"
              />
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm text-[#0B2D2D]/60">
              <MapPin size={16} weight="duotone" className="text-[#00A091]" />
              <span>Unit 14 Halstead Park, 15 Halstead Road, Mkodeni, Pietermaritzburg, 3201, South Africa</span>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
